import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import {
  CreateAndUpdateBookingDriverDtoInput,
  useGetBookingAllocatedDriverQuery,
  useReplaceAllocatedDriverMutation,
  useUpdateAllocatedDriverMutation,
} from 'generated/graphql'
import {
  GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
  GET_BOOKING_TOTAL_DRIVER_CHARGE,
  GET_BOOKING_ALLOCATED_DRIVERS,
} from 'graphql/queries'

import { EditDriverForm } from './EditDriverForm'

interface IProps {
  bookingId: number
  bookingDriverId: number
  openDialog: boolean
  handleCloseDialog: () => any
}

const EditDriverDialog = (props: IProps) => {
  const { bookingId, bookingDriverId, openDialog, handleCloseDialog } = props

  const methods = useForm<CreateAndUpdateBookingDriverDtoInput>({
    defaultValues: {
      driverId: '',
      vehicleId: '',
      charge: 0,
      extraPayment: 0,
      xeroPurchaseCode: '',
      currencyId: '',
      isShownInstruction: false,
      instruction: '',
    },
  })

  const { handleSubmit, reset } = methods
  const hasDirtyFields = Object.keys(methods.formState.dirtyFields).length > 0

  // Queries
  const { data: bookingDriverData, loading: bookingDriverLoading } =
    useGetBookingAllocatedDriverQuery({
      variables: { bookingDriverId },
      skip: !openDialog,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        if (response.bookingAllocatedDriver) {
          console.log('resetting form with booking allocated driver data')
          reset({
            ...response.bookingAllocatedDriver,
            xeroPurchaseCode: response.bookingAllocatedDriver?.xeroPurchaseCode ?? '',
            instruction: response.bookingAllocatedDriver?.instruction ?? '',
            isShownInstruction: response.bookingAllocatedDriver?.isShownInstruction ?? false,
          })
        }
      },
    })
  const driver = bookingDriverData?.bookingAllocatedDriver?.driver
  const parentDriverId = driver?.primaryDriverId
    ? Number(driver?.primaryDriverId)
    : Number(driver?.id)

  const refetchQueries = [
    {
      query: GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
      variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
    },
    {
      query: GET_BOOKING_TOTAL_DRIVER_CHARGE,
      variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
    },
    {
      query: GET_BOOKING_ALLOCATED_DRIVERS,
      variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
    },
  ]

  // Mutations
  const [updateDriver, { loading: isUpdatingDriver }] = useUpdateAllocatedDriverMutation({
    refetchQueries,
    onCompleted: (data) => {
      handleCloseDialog()

      toast.success(`Driver (${data.updateAllocatedDriver?.driver.name}) was updated with success.`)
    },
  })

  const [replaceDriver, { loading: isReplacingDriver }] = useReplaceAllocatedDriverMutation({
    refetchQueries,
    onCompleted: (data) => {
      handleCloseDialog()

      toast.success(
        `Driver (${data.replaceAllocatedDriver?.driver.name}) was replaced with success.`,
      )
    },
  })

  const isSaving = isUpdatingDriver || isReplacingDriver

  const handleOnSuccess: SubmitHandler<CreateAndUpdateBookingDriverDtoInput> = (formData) => {
    const {
      charge,
      extraPayment,
      xeroPurchaseCode,
      currencyId,
      isShownInstruction,
      instruction,
      driverId: selectedDriverId,
    } = formData
    const currentBookingDriver = bookingDriverData?.bookingAllocatedDriver

    if (currentBookingDriver) {
      const input = {
        id: currentBookingDriver.id,
        bookingId: currentBookingDriver.bookingId,
        driverId: selectedDriverId || currentBookingDriver.driverId,
        vehicleId: currentBookingDriver.vehicleId,
        charge: Number(charge),
        extraPayment: Number(extraPayment),
        xeroPurchaseCode,
        currencyId,
        isShownInstruction,
        instruction,
      }

      if (Number(input.driverId) === Number(currentBookingDriver.driverId)) {
        updateDriver({ variables: { input } })
      } else {
        replaceDriver({ variables: { input } })
      }
    }
  }

  const handleClose = () => {
    handleCloseDialog()
  }

  const handleSubmitForm = () => {
    handleSubmit(handleOnSuccess)()
  }

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openDialog}
      aria-labelledby='edit-allocated-driver-dialog-title'>
      <FplDialogTitle id='edit-allocated-driver-dialog-title' onClose={handleClose}>
        <Typography mb={0} paragraph variant='h4'>
          Edit Allocated Driver
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          <form id='edit-allocated-driver-dialog-form' noValidate>
            <EditDriverForm
              bookingId={bookingId}
              loading={bookingDriverLoading}
              driverId={Number(driver?.id)}
              parentDriverId={parentDriverId}
            />
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          variant='contained'
          form='edit-allocated-driver-dialog-form'
          loading={isSaving}
          disabled={!hasDirtyFields || isSaving || bookingDriverLoading}
          onClick={handleSubmitForm}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditDriverDialog
